import React, { Component } from 'react';
import { DataSource } from '~/Api/DataSource';
import { HrMaxPrice } from '~/Api/User/ProductGroup/HrMaxPrice';
import { ComparativePriceProduct } from '~/Api/User/ProductGroup/HrMaxPrice/ComparativePriceProduct';
import { formatCountriesError } from '~/UI';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading';

import styles from './styles.less';

interface Props {
	dataSources: {
		dataSources: DataSource[];
		products: ComparativePriceProduct[];
	}[];
	hrMaxPrice: HrMaxPrice;
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	private renderEmptyResultReason() {
		const {
			hrMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.findMaxOneReferenceProductError();

		if (finalCalculation.hasMissingProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.requiresMinOneReferenceProductErrorCode()) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at least one reference country product or at least three EU+EEA products with prices
				</strong>
			);
		}
		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each reference country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.isMissingReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.isIllegalProductPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		return null;
	}

	public render() {
		const {
			hrMaxPrice: { finalCalculation },
			dataSources,
			formatPercentage,
		} = this.props;

		return (
			<div className={styles.container}>
				<div className={styles.columns}>
					<div className={styles.leftColumn}>
						<Heading level={3}>Applicable reference prices:</Heading>
						{dataSources.map((row) => (
							<div className={styles.row} key={row.dataSources[0].country.alpha2}>
								<span className={styles.countryIcon}>
									<CountryIcon code={row.dataSources[0].country.alpha2} name={row.dataSources[0].country.name} />
								</span>

								<span className={styles.countryDescription}>
									{row.dataSources.map((dataSource) => dataSource.shortName).join(', ')}
									{row.products.length > 1 ? (
										<span className={styles.productsCount}>(average of {row.products.length} prices)</span>
									) : null}
								</span>
							</div>
						))}
					</div>
					<div className={styles.centerColumn}>
						<Heading level={3}>Calculated max price:</Heading>
						<div className={styles.row}>
							<p className={styles.rowName}>Calculated comparative drug price:</p>
							<p className={styles.rowValue}>
								{finalCalculation.comparativePrice !== null ? (
									<strong>{finalCalculation.comparativePrice.price.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
						{finalCalculation.thirdPriceDeviation !== null ? (
							<div className={styles.row}>
								<p className={styles.rowName}>Third price deviation:</p>
								<p className={styles.rowValue}>
									<strong>{formatPercentage(finalCalculation.thirdPriceDeviation.deviationPercents)}</strong>
								</p>
							</div>
						) : null}
						{finalCalculation.emptyResultReason.length > 0 ? (
							<div className={styles.row}>
								<p>{this.renderEmptyResultReason()}</p>
							</div>
						) : null}
					</div>
					<div className={styles.rightColumn}>
						<Heading level={3}>&nbsp;</Heading>
						<div className={styles.row}>
							<p className={styles.smallRowName}>
								Current wholesale price
								{finalCalculation.referencedProduct !== null
									? ` (${finalCalculation.referencedProduct.dataSource.shortName})`
									: null}
							</p>
							<p className={styles.smallRowValue}>
								{finalCalculation.referencedProduct !== null &&
								finalCalculation.referencedProduct.currentWholesalePrice !== null ? (
									<strong>{finalCalculation.referencedProduct.currentWholesalePrice.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.smallRowName}>Potential price drop:</p>
							<p className={styles.smallRowValue}>
								{finalCalculation.potentialPriceDropPercents !== null ? (
									<strong>{formatPercentage(finalCalculation.potentialPriceDropPercents)}</strong>
								) : null}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
